import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ResourceCell from "./ResourceCell";
import SettingCell from "./SettingCell";
import GroupByCell from "./GroupByCell";
import { useColumns } from "../../../../hooks/grid/useColumns";
import { useScrollContext } from "../../../../hooks/context/useScrollContext";
import { useScheduleContext } from "../../../../hooks/context/useScheduleContext";
import { getResourceRowHeight } from "../../../../helpers/grid/getResourceRowHeight";
import { getGroupRowStyle, hasGroupDivider } from "../../../../helpers/grid/getGroupRowStyle";
import { getPrimaryActiveResource } from "../../../../../../../utils/helpers/user";
import { cn } from "../../../../../../../lib/utils";

function ColumnsList() {
    const { t } = useTranslation();
    const baseColumns = useColumns();
    const {
        columnsContainerRef,
        columnsContentRef,
        scrollbarHeight,
        handlePaneWheel
    } = useScrollContext();

    const { loading, resources, resourceLayouts, columns: columnsConfig } = useScheduleContext();

    const columns = useMemo(() => {
        return baseColumns.filter((col: any) => {
            return typeof columnsConfig?.[col.field] === "boolean"
                ? columnsConfig[col.field]
                : col.field === "name";
        });
    }, [baseColumns, columnsConfig]);
    return (
        <div
            ref={columnsContainerRef}
            className="employee-list-container"
            style={{
                paddingBottom: `${scrollbarHeight}px`,
                pointerEvents: loading ? "none" : "auto",
                opacity: loading ? 0.5 : 1,
            }}
            onWheel={handlePaneWheel}
        >
            <div className="employee-list-content" ref={columnsContentRef}>
                {!resources.length || resources.length === 0 ? (
                    <div className="no-data-message">{t("no-data-to-display")}</div>
                ) : (
                    <div
                        className="employee-grid"
                        style={{
                            gridTemplateColumns: columns
                                .map((col: any) =>
                                    col.field === "name"
                                        ? "minmax(150px, 1fr)"
                                        : "minmax(90px, 150px)"
                                )
                                .join(" "),
                        }}
                    >
                        {resources.flatMap((resource, resourceIndex) =>
                            columns.map((column: any, index: number) => {
                                const withDivider = hasGroupDivider(resources, resourceIndex);
                                if ((resource as any)?.type === "view-by" && index === 0) {
                                    const style = { height: "40px" };
                                    return (
                                        <GroupByCell
                                            key={`${resource.id}-${column.field}`}
                                            style={style}
                                            value={resource}
                                            withDivider={withDivider}
                                        />
                                    )
                                }
                                if (column.field === "name") {
                                    return (
                                        <ResourceCell
                                            key={`${resource.id}-${column.field}`}
                                            resource={resource}
                                            rowHeight={getResourceRowHeight(resource.id, resourceLayouts)}
                                            column={column}
                                        />
                                    );
                                }

                                const value = column.field === "job"
                                    ? getPrimaryActiveResource((resource as any)?.jobs)
                                    : column.field === "location"
                                        ? getPrimaryActiveResource((resource as any)?.locations)
                                        : (resource as any)?.level1 ||
                                        (resource as any)?.level2 ||
                                        (resource as any)?.level3 ||
                                        (resource as any)?.level4;

                                const isGroupRow = (resource as any)?.type === "view-by";
                                let style: any = { height: getResourceRowHeight(resource.id, resourceLayouts) };
                                if (columns.length <= 2) style.borderRight = 0;
                                if (isGroupRow) {
                                    style = { ...style, height: "40px", ...getGroupRowStyle(withDivider) };
                                }

                                return (
                                    <div
                                        key={`${resource.id}-${column.field}`}
                                        className={cn("employee-info-cell", isGroupRow && "tw-bg-primary/10")}
                                        style={style}
                                    >
                                        <SettingCell value={value} field={column.field} />
                                    </div>
                                );
                            }),
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ColumnsList;
